import React, { useCallback, useEffect, useMemo, useState } from "react";
import YhTable, { YhTableColumn } from "../../../ui/YhTable";
import YhInput from "../../../ui/YhInput";
import YhSelect from "../../../ui/YhSelect";
import YhButton from "../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../helpers/methods";
import { getUnmergeLeadList, promoteUnmergeLeads } from "./UnmergedService";
import { YhSourceIcon } from "../../../ui/YhSourceIcon";
import {
  getCachedOrgName,
  loadTenantOrganizationsOnce,
} from "../../../Shared/Organization/orgNameCache";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../../redux/authSlice";

const outlineBtn =
  "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-[#E5E7EB] bg-white px-4 text-sm font-semibold text-[#374151] transition hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50";

const UnmergedLeads = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState<"pending" | "done" | "all">("pending");
  const [uniqueOnly, setUniqueOnly] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  /** When true, promote uses server-side filter (all pages), not just selectedIds */
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [orgNamesReady, setOrgNamesReady] = useState(false);
  const activeOrganizationId = useSelector(selectActiveOrganizationId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUnmergeLeadList({
        page,
        limit: pageSize,
        search,
        source,
        status,
        uniqueOnly,
        sortBy,
        sortDir,
      });
      if (!(res?.success === 1 || res?.success === true)) {
        openErrorNotification(res?.message || "Failed to load unmerged leads");
        setRows([]);
        return;
      }
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotal(Number(res?.metadata?.total || 0));
      setPendingCount(Number(res?.metadata?.pendingCount || 0));
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load unmerged leads");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, source, status, uniqueOnly, sortBy, sortDir, activeOrganizationId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    loadTenantOrganizationsOnce().finally(() => {
      if (alive) setOrgNamesReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
    setSelectAllMatching(false);
  }, [search, source, status, uniqueOnly, sortBy, sortDir, activeOrganizationId]);

  const pageIds = useMemo(
    () => rows.map((r) => String(r._id)).filter(Boolean),
    [rows]
  );
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const matchingCount =
    status === "pending" ? total : pendingCount;
  const selectedCount = selectAllMatching
    ? matchingCount
    : selectedIds.length;

  const clearSelection = () => {
    setSelectAllMatching(false);
    setSelectedIds([]);
  };

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) =>
      checked
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((x) => x !== id)
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      clearSelection();
      return;
    }
    setSelectAllMatching(false);
    setSelectedIds(Array.from(new Set([...selectedIds, ...pageIds])));
  };

  const selectAllPagesMatching = () => {
    setSelectAllMatching(true);
    setSelectedIds(pageIds);
  };

  const moveSelected = async () => {
    if (!selectAllMatching && !selectedIds.length) {
      openErrorNotification("Select at least one lead");
      return;
    }
    const countLabel = selectAllMatching
      ? matchingCount
      : selectedIds.length;
    if (
      !window.confirm(
        `Move ${countLabel} pending lead(s)${
          selectAllMatching ? " matching current filters (all pages)" : ""
        } to All Leads?`
      )
    ) {
      return;
    }
    setMoving(true);
    try {
      const res = selectAllMatching
        ? await promoteUnmergeLeads({
            allMatching: true,
            source: source || undefined,
            search: search || undefined,
          })
        : await promoteUnmergeLeads({ ids: selectedIds });
      if (!(res?.success === 1 || res?.success === true)) {
        openErrorNotification(res?.message || "Move failed");
        return;
      }
      openSuccessNotification(res.message || "Moved to All Leads");
      clearSelection();
      await load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Move failed");
    } finally {
      setMoving(false);
    }
  };

  const moveAllPending = async () => {
    if (
      !window.confirm(
        `Move all pending unmerged leads${source ? ` from ${source}` : ""} to All Leads?`
      )
    ) {
      return;
    }
    setMoving(true);
    try {
      const res = await promoteUnmergeLeads({
        allPending: true,
        source: source || undefined,
      });
      if (!(res?.success === 1 || res?.success === true)) {
        openErrorNotification(res?.message || "Bulk move failed");
        return;
      }
      openSuccessNotification(res.message || "Bulk move done");
      clearSelection();
      await load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Bulk move failed");
    } finally {
      setMoving(false);
    }
  };

  const columns: YhTableColumn[] = [
    {
      field: "_select",
      headerName: (
        <input
          type="checkbox"
          checked={selectAllMatching || allPageSelected}
          onChange={(e) => toggleSelectAll(e.target.checked)}
          aria-label="Select all on page"
        />
      ),
      width: 48,
      minWidth: 48,
      renderCell: ({ row }) => (
        <input
          type="checkbox"
          checked={
            selectAllMatching || selectedIds.includes(String(row._id))
          }
          onChange={(e) => {
            e.stopPropagation();
            toggleSelect(String(row._id), e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      field: "lead_name",
      headerName: "Name",
      minWidth: 140,
      renderCell: ({ row }) => (
        <span className="font-medium text-[#111827]">
          {row.lead_name || "—"}
        </span>
      ),
    },
    {
      field: "lead_phone",
      headerName: "Phone",
      minWidth: 120,
      renderCell: ({ row }) => row.lead_phone || "—",
    },
    {
      field: "lead_email",
      headerName: "Email",
      minWidth: 160,
      renderCell: ({ row }) => (
        <span className="truncate text-[#6B7280]">{row.lead_email || "—"}</span>
      ),
    },
    {
      field: "organizationId",
      headerName: "Organization",
      minWidth: 140,
      renderCell: ({ row }) =>
        (orgNamesReady && getCachedOrgName(row.organizationId)) || "—",
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 130,
      renderCell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5">
          <YhSourceIcon source={row.source || ""} />
          {row.source || "—"}
        </span>
      ),
    },
    {
      field: "project_name",
      headerName: "Project",
      minWidth: 140,
      renderCell: ({ row }) => row.project_name || row.project_id || "—",
    },
    {
      field: "statusLabel",
      headerName: "Status",
      minWidth: 100,
      renderCell: ({ row }) => (
        <span
          className={`inline-flex rounded-lg px-2 py-1 text-xs font-semibold ${
            row.isPending
              ? "bg-[#FEF3C7] text-[#92400E]"
              : "bg-[#D1FAE5] text-[#065F46]"
          }`}
        >
          {row.statusLabel}
          {row.dupCount > 1 ? ` · ${row.dupCount}x` : ""}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Received",
      minWidth: 150,
      renderCell: ({ row }) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—",
    },
    {
      field: "_actions",
      headerName: "Action",
      minWidth: 110,
      renderCell: ({ row }) =>
        row.isPending ? (
          <button
            type="button"
            className="text-sm font-semibold text-[#A02448] hover:underline"
            disabled={moving}
            onClick={async (e) => {
              e.stopPropagation();
              setMoving(true);
              try {
                const res = await promoteUnmergeLeads({
                  ids: [String(row._id)],
                });
                if (res?.success) {
                  openSuccessNotification("Moved to All Leads");
                  await load();
                } else {
                  openErrorNotification(res?.message || "Move failed");
                }
              } catch (err: any) {
                openErrorNotification(err?.message || "Move failed");
              } finally {
                setMoving(false);
              }
            }}
          >
            Move to leads
          </button>
        ) : (
          <span className="text-xs text-[#9CA3AF]">Done</span>
        ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-[#111827]">
            Unmerged Leads
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Staging inbox only. After you move a lead to All Leads it is{" "}
            <span className="font-semibold text-[#111827]">removed</span> from
            here. Pending:{" "}
            <span className="font-semibold text-[#111827]">{pendingCount}</span>
            {" · "}
            Showing:{" "}
            <span className="font-semibold text-[#111827]">{total}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={outlineBtn}
            disabled={(!selectAllMatching && !selectedIds.length) || moving}
            onClick={moveSelected}
          >
            Move selected ({selectedCount})
          </button>
          <YhButton disabled={moving || !pendingCount} onClick={moveAllPending}>
            {moving ? "Moving…" : "Move all pending"}
          </YhButton>
        </div>
      </div>

      {allPageSelected && total > pageIds.length && !selectAllMatching ? (
        <div className="mb-3 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-2.5 text-sm text-[#92400E]">
          Selected {pageIds.length} on this page.{" "}
          <button
            type="button"
            className="font-semibold underline"
            onClick={selectAllPagesMatching}
          >
            Select all {matchingCount} matching filters (all pages)
          </button>
        </div>
      ) : null}
      {selectAllMatching ? (
        <div className="mb-3 rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] px-4 py-2.5 text-sm text-[#065F46]">
          All {matchingCount} pending leads matching filters are selected.{" "}
          <button
            type="button"
            className="font-semibold underline"
            onClick={clearSelection}
          >
            Clear
          </button>
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <YhInput
            label="Search"
            placeholder="Name, phone, email, project…"
            value={search}
            onChange={(e: any) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-[160px]">
          <YhSelect
            label="Source"
            value={source}
            onChange={(e: any) => setSource(e.target.value || "")}
            options={[
              { label: "All sources", value: "" },
              { label: "Facebook", value: "Facebook" },
              { label: "Magic Bricks", value: "Magic Bricks" },
              { label: "99 Acres", value: "99 Acres" },
              { label: "Housing", value: "Housing" },
              { label: "Website", value: "Website" },
              { label: "Hometatva", value: "Hometatva" },
            ]}
          />
        </div>
        <div className="w-[140px]">
          <YhSelect
            label="Status"
            value={status}
            onChange={(e: any) =>
              setStatus((e.target.value || "pending") as any)
            }
            options={[
              { label: "Pending (inbox)", value: "pending" },
              { label: "All", value: "all" },
            ]}
          />
        </div>
        <div className="w-[150px]">
          <YhSelect
            label="Sort by"
            value={sortBy}
            onChange={(e: any) => setSortBy(e.target.value || "createdAt")}
            options={[
              { label: "Received", value: "createdAt" },
              { label: "Name", value: "lead_name" },
              { label: "Phone", value: "lead_phone" },
              { label: "Source", value: "source" },
              { label: "Project", value: "project_name" },
            ]}
          />
        </div>
        <div className="w-[120px]">
          <YhSelect
            label="Order"
            value={sortDir}
            onChange={(e: any) =>
              setSortDir((e.target.value || "desc") as "asc" | "desc")
            }
            options={[
              { label: "Newest", value: "desc" },
              { label: "Oldest", value: "asc" },
            ]}
          />
        </div>
        <label className="mb-1 flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm text-[#374151]">
          <input
            type="checkbox"
            checked={uniqueOnly}
            onChange={(e) => setUniqueOnly(e.target.checked)}
          />
          Unique per organization
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-[#F0E6D8] bg-white">
        <YhTable
          columns={columns}
          rows={rows}
          getRowId={(r) => r._id}
          loading={loading}
          emptyMessage="No unmerged leads"
          page={page}
          pageSize={pageSize}
          totalRows={total}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(0);
          }}
          paginationLabel="unmerged"
        />
      </div>
    </div>
  );
};

export default UnmergedLeads;
